// ============================================================================
// NPSDPersonScreen.tsx - Main orchestrating screen for the NPSD Person entity
// Converted from Vue 2 Vue-NPSDPerson.ts (1543 lines)
//
// Manages tabs, edit/save lifecycle, connections, attachments, warnings,
// profile pic, citations, address validation, and coordinates all
// child components.
// ============================================================================
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useNPSDEntity } from '@/hooks/useNPSDEntity';
import { NPSDTabBar } from '@/components/npsd/NPSDTabBar';
import { NPSDWarningsDisplay } from '@/components/npsd/NPSDWarningsDisplay';
import { NPSDPersonDetails } from '@/components/npsd/NPSDPersonDetails';
import { NPSDPersonConnections } from '@/components/npsd/NPSDPersonConnections';
import { NPSDPersonHistory } from '@/components/npsd/NPSDPersonHistory';
import { PersonCitationsGrid } from '@/components/citation/PersonCitationsGrid';
import { NPSDAttachments } from '@/components/npsd/NPSDAttachments';
import { NPSDWarnings } from '@/components/npsd/NPSDWarnings';
import { NPSDAuditLog } from '@/components/npsd/NPSDAuditLog';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SearchModal } from '@/components/modals/SearchModal';
import { PageModal } from '@/components/modals/PageModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { NPSDBusinessScreen } from '@/components/npsd/NPSDBusinessScreen';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import {
  refreshPage,
  refreshWithParams,
  navigateToRecord,
  navigateToMapByAddress,
} from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { currentUserHasPermission, getModuleType, hasValue, hasStringValue } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { Codes } from '@/constants/codes';

// ── Global declarations (lazy lookups to avoid capturing before registration) ──
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
import { RelType } from '@/models/other-entities';
import { getAuthObject } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
const showUsaStateInformation = _wg('showUsaStateInformation');
const getCompanyCountry = _wg('getCompanyCountry');

const isBasePageCaseReport = _wg('isBasePageCaseReport');
const isBasePageCallCard = _wg('isBasePageCallCard');
const isBasePageCitation = _wg('isBasePageCitation');
const setNPSDItemLocked = _wg('setNPSDItemLocked');
const updateNPSDItemForReport = _wg('updateNPSDItemForReport');
const updateNPSDItemForCallCard = _wg('updateNPSDItemForCallCard');
let currentPerson = (window as any).currentPerson;

// ── Props interface ─────────────────────────────────────────────────────
interface NPSDPersonScreenProps {
  personId?: number | string | null;
  isWindow?: boolean;
  isUnknown?: boolean;
  startInEditMode?: boolean;
  onClose?: () => void;
  onPersonSaved?: (result: any) => void;
  onPersonUpdated?: (result: any) => void;
}

// ── Component ───────────────────────────────────────────────────────────
export function NPSDPersonScreen({
  personId: personIdProp = null,
  isWindow = false,
  isUnknown = false,
  startInEditMode = false,
  onClose,
  onPersonSaved,
  onPersonUpdated,
}: NPSDPersonScreenProps) {
  const { theme } = useTheme();

  // ── Core state ──────────────────────────────────────────────────────
  const [person, setPerson] = useState<any>(null);
  const [originalPerson, setOriginalPerson] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaveMode, setIsSaveMode] = useState(false);
  const [isCaseReportMode, setIsCaseReportMode] = useState(false);
  const [activeTab, setActiveTab] = useState('person');

  // ── Mobile detection ────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(false);

  // ── Permissions ─────────────────────────────────────────────────────
  const canUpdate = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE), []);
  const canSave = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE), []);
  const canCreateCitation = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE), []);
  const [canViewCitations, setCanViewCitations] = useState(false);

  // ── USA state info flag ─────────────────────────────────────────────
  const [showUsaStateInfo, setShowUsaStateInfo] = useState(false);

  // ── Code lists ──────────────────────────────────────────────────────
  const [genderCodes, setGenderCodes] = useState<any[]>([]);
  const [raceCodes, setRaceCodes] = useState<any[]>([]);
  const [eyeColorCodes, setEyeColorCodes] = useState<any[]>([]);
  const [hairColorCodes, setHairColorCodes] = useState<any[]>([]);
  const [heightUnitsList, setHeightUnitsList] = useState<any[]>([]);
  const [weightUnitsList, setWeightUnitsList] = useState<any[]>([]);
  const [locStates, setLocStates] = useState<any[]>([]);
  const [phoneOptionTypes, setPhoneOptionTypes] = useState<any[]>([]);
  const [emailOptionTypes, setEmailOptionTypes] = useState<any[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<any[]>([]);

  // ── Confirm modal ───────────────────────────────────────────────────
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null as (() => void) | null,
  });

  // ── useNPSDEntity hook ──────────────────────────────────────────────
  const {
    showConnectionSearchModal, connectionSearchType, openConnectionSearch, closeConnectionSearchModal,
    showViewPersonModal, viewPersonId, setShowViewPersonModal,
    showViewVehicleModal, viewVehicleId, setShowViewVehicleModal,
    showViewBusinessModal, viewBusinessId, setShowViewBusinessModal,
    showViewClientModal, viewClientId, setShowViewClientModal,
    openViewPersonModal, openViewVehicleModal, openViewBusinessModal, openViewClientModal,
    handleConnectionView,
    validateAndFillAddress,
    ensureConnectionsNewPopulated,
    handleAttachmentUpload,
    removeAttachment,
  } = useNPSDEntity({
    entityType: 'person',
    setEntity: setPerson,
    connectionTypes: ['people', 'vehicles', 'businesses', 'clients'],
  });

  // ── Relationship dialog (person-to-person) ──────────────────────────
  const [showRelationshipDialog, setShowRelationshipDialog] = useState(false);
  const [pendingConnectionItem, setPendingConnectionItem] = useState<any>(null);
  const [selectedRelationship, setSelectedRelationship] = useState<number | null>(null);
  const [connectionNotes, setConnectionNotes] = useState('');


  // ── Refs ────────────────────────────────────────────────────────────
  const profilePicInputRef = useRef<HTMLInputElement>(null);
  const attachmentsFileInputRef = useRef<HTMLInputElement>(null);
  const personRef = useRef<any>(null);

  // Keep personRef in sync for use in callbacks
  personRef.current = person;

  // ── Computed values ─────────────────────────────────────────────────
  const personDisplayName = useMemo(() => {
    if (!person) return '';
    return `${person.lastName || ''}, ${person.firstName || ''}`.trim();
  }, [person]);

  const age = useMemo(() => {
    if (!person?.dob) return '';
    if (typeof moment !== 'undefined') {
      return (moment as any).duration(moment().utc().diff(moment(person.dob).utc())).format('Y');
    }
    return '';
  }, [person?.dob]);

  const showEditButton = useMemo(() => {
    if (!person || !hasValue(person.id)) return false;
    if (getAuthObject().company_id !== person.createdByCompanyId) return false;
    return !isEditMode && canUpdate;
  }, [person, isEditMode, canUpdate]);

  const showUpdateButton = useMemo(() => {
    return hasValue(person?.id) && isEditMode && canUpdate;
  }, [person?.id, isEditMode, canUpdate]);

  const showCancelButton = useMemo(() => showUpdateButton, [showUpdateButton]);

  const showSaveButton = useMemo(() => {
    return !hasValue(person?.id) && !isCaseReportMode && canSave;
  }, [person?.id, isCaseReportMode, canSave]);

  const showCaseReportSaveButton = useMemo(() => {
    return !hasValue(person?.id) && isCaseReportMode && canSave;
  }, [person?.id, isCaseReportMode, canSave]);

  const enableFormField = useMemo(() => isEditMode || isSaveMode, [isEditMode, isSaveMode]);

  const showAddConnectionButton = useMemo(() => isEditMode || isSaveMode, [isEditMode, isSaveMode]);

  const connectionSearchTypeLabel = useMemo(() => {
    const labels: Record<string, string> = {
      'person': 'Person',
      'vehicle': 'Vehicle',
      'business': 'Business',
      'client': 'Client',
    };
    return labels[connectionSearchType] || '';
  }, [connectionSearchType]);

  const showAddAttachmentButton = useMemo(() => isEditMode || isSaveMode, [isEditMode, isSaveMode]);

  const canEditProfilePic = useMemo(() => {
    return (isEditMode && canUpdate) || (isSaveMode && canSave);
  }, [isEditMode, isSaveMode, canUpdate, canSave]);

  const profilePicUrl = useMemo(() => {
    if (!person) return '/Content/Images/defaultPerson.png';
    const attachmentId = person.descriptionPicId;
    if (!hasValue(attachmentId) || !person.attachmentList) {
      return '/Content/Images/defaultPerson.png';
    }
    const attachment = person.attachmentList.find((a: any) => a.id === attachmentId);
    if (!attachment || !attachment.signedURL) {
      return '/Content/Images/defaultPerson.png';
    }
    return attachment.signedURL;
  }, [person]);

  const isOwnCompanyPerson = useMemo(() => {
    if (!person) return false;
    return getAuthObject().company_id === person.createdByCompanyId;
  }, [person]);

  const connectionsCount = useMemo(() => {
    if (!person?.connections) return 0;
    const c = person.connections;
    return (c.people?.length || 0) + (c.vehicles?.length || 0) + (c.businesses?.length || 0) + (c.clients?.length || 0);
  }, [person?.connections]);

  const tabsConfig = useMemo(() => [
    { id: 'person', label: 'Person' },
    { id: 'connections', label: 'Connections', badge: connectionsCount },
    { id: 'history', label: 'History' },
    { id: 'attachments', label: 'Attachments', badge: person?.attachmentList?.length || 0 },
    { id: 'warnings', label: 'Warnings', badge: person?.warnings?.length || 0 },
    { id: 'citations', label: 'Citations', badge: person?.citationCount || 0, visible: canViewCitations },
    { id: 'auditlog', label: 'Audit Log' },
  ], [connectionsCount, person?.attachmentList?.length, person?.warnings?.length, person?.citationCount, canViewCitations]);

  // ── Helper functions ────────────────────────────────────────────────

  const updateGlobalCurrentPerson = useCallback((personData: any) => {
    if (isWindow) return;
    currentPerson = personData;
    (window as any).currentPerson = personData;
    if (typeof App !== 'undefined') {
      App.originalPersonResult = JSON.parse(JSON.stringify(personData));
    }
  }, [isWindow]);

  const transformPersonFromApi = useCallback((result: any): any => {
    const p = { ...result };

    // Initialize connections if needed
    if (!p.connections) {
      p.connections = { people: [], vehicles: [], businesses: [], clients: [], reports: [] };
    }
    p.connectionsOld = JSON.parse(JSON.stringify(p.connections));
    p.connectionsNew = { people: [], vehicles: [], businesses: [], clients: [], reports: [] };

    // Initialize height/weight
    if (!p.height) {
      p.height = { displayUnits: 'ft', mm: null };
    }
    if (!p.weight) {
      p.weight = { displayUnits: 'lbs', grams: null };
    }

    // Calculate display values for height
    if (p.height.displayUnits === 'm' || p.height.displayUnits === 'meters') {
      p.personHeightMeters = p.height.mm ? (p.height.mm * 0.001).toFixed(2) : '';
      p.personHeightFeet = '';
      p.personHeightInches = '';
    } else {
      if (p.height.mm) {
        const inches = Math.round(p.height.mm / 25.4);
        p.personHeightFeet = Math.floor(inches / 12).toString();
        p.personHeightInches = (inches % 12).toString();
      } else {
        p.personHeightFeet = '';
        p.personHeightInches = '';
      }
      p.personHeightMeters = '';
    }

    // Calculate display value for weight
    if (p.weight.grams) {
      if (p.weight.displayUnits === 'kg') {
        p.personWeight = (p.weight.grams / 1000).toFixed(1);
      } else {
        p.personWeight = (p.weight.grams / 453.592).toFixed(1);
      }
    } else {
      p.personWeight = '';
    }

    // Initialize arrays if missing
    if (!p.attachmentList) p.attachmentList = [];
    if (!p.warnings) p.warnings = [];

    // Initialize citation count
    p.citationCount = 0;

    // Build attachmentInfoList from attachmentList
    p.attachmentInfoList = [];
    for (let i = 0; i < p.attachmentList.length; i++) {
      p.attachmentInfoList.push({
        id: p.attachmentList[i].id,
        activationKey: '',
      });
    }
    if (!p.phoneNumbers) p.phoneNumbers = [];
    if (!p.emails) p.emails = [];

    // Ensure descriptionPicInfoId matches descriptionPicId
    p.descriptionPicInfoId = p.descriptionPicId;

    return p;
  }, []);

  const initNewPerson = useCallback(() => {
    const newPerson = {
      id: null,
      firstName: isUnknown ? 'Unknown' : '',
      lastName: isUnknown ? 'Unknown' : '',
      middleName: '',
      suffix: '',
      aliases: '',
      dob: null,
      gender: null,
      race: null,
      eyeColor: null,
      hairColor: null,
      height: { displayUnits: 'ft', mm: null },
      weight: { displayUnits: 'lbs', grams: null },
      personHeightFeet: '',
      personHeightInches: '',
      personHeightMeters: '',
      personWeight: '',
      notes: '',
      // Address
      locFormattedAddress: '',
      locAddress: '',
      locAddress2: '',
      locName: '',
      locCity: '',
      locState: '',
      locZip: '',
      locCountry: '',
      latitude: null as number | null,
      longitude: null as number | null,
      customLocationId: null as number | null,
      // Contact
      phoneNumbers: [],
      emails: [],
      // Government ID
      driversLicenseType: null,
      driversLicenseNum: '',
      driversLicenseClass: '',
      driversLicenseState: '',
      driversLicenseIssued: null,
      driversLicenseExpires: null,
      driversLicenseRestrictions: '',
      // Employment
      employer: '',
      employeeId: '',
      // Connections
      connections: {
        people: [],
        vehicles: [],
        businesses: [],
        clients: [],
        reports: [],
      },
      connectionsOld: { people: [], vehicles: [], businesses: [], clients: [], reports: [] },
      connectionsNew: { people: [], vehicles: [], businesses: [], clients: [], reports: [] },
      // Warnings
      warnings: [],
      // Attachments
      attachmentList: [],
      attachmentInfoList: [],
      descriptionPicId: null,
      descriptionPicInfoId: null,
      // Meta
      createdByCompanyId: getAuthObject().company_id,
      createdByCompanyName: '',
      citationCount: 0,
    };

    setPerson(newPerson);
    setOriginalPerson(JSON.parse(JSON.stringify(newPerson)));
    if (isWindow) {
      setIsCaseReportMode(true);
    }
    setIsSaveMode(true);
    setIsEditMode(false);
    setIsLoading(false);
    setCanViewCitations(false);
    updateGlobalCurrentPerson(newPerson);
  }, [isUnknown, isWindow, updateGlobalCurrentPerson]);

  const loadCitationCount = useCallback((personData: any) => {
    if (!personData?.id) return;

    api.getCitationsForPerson(
      personData.id,
      { sortField: 'dateOfIssuance', sortDir: 'desc' },
      0, 1,
      (result: any) => {
        const count = result.records || 0;
        setPerson((prev: any) => {
          if (!prev) return prev;
          return { ...prev, citationCount: count };
        });
        // Keep originalPersonResult in sync so the dirty check doesn't trigger
        if (App.originalPersonResult) {
          App.originalPersonResult.citationCount = count;
        }
      },
      () => {
        // Silently fail - citation count is non-critical
      }
    );
  }, []);

  const loadPerson = useCallback((id: string) => {
    setIsLoading(true);

    api.getNPSDItemById(id, 'person', (result: any) => {
      const transformed = transformPersonFromApi(result);
      setPerson(transformed);
      setOriginalPerson(JSON.parse(JSON.stringify(transformed)));

      // Check citation permissions
      const isSameCompany = getAuthObject().company_id === transformed.createdByCompanyId;
      const citationViewable = isSameCompany && currentUserHasPermission(AccessAuthorities.ROLE_CITATION_VIEW);
      setCanViewCitations(citationViewable);

      if (citationViewable) {
        loadCitationCount(transformed);
      }

      setIsEditMode(false);
      setIsSaveMode(false);
      setIsLoading(false);

      updateGlobalCurrentPerson(transformed);

      if (startInEditMode) {
        // Lock and enter edit mode
        setNPSDItemLocked(transformed.id.toString(), true, 'person', () => {
          setIsEditMode(true);
          setIsSaveMode(false);
        });
      }
    });
  }, [transformPersonFromApi, loadCitationCount, updateGlobalCurrentPerson, startInEditMode]);

  // ── Code lists loading ──────────────────────────────────────────────
  const loadCodeLists = useCallback(() => {
    if (typeof Codes !== 'undefined') {
      setGenderCodes(Codes.getGenderCodes() || []);
      setEyeColorCodes(Codes.getEyeColorCodes() || []);
      setHairColorCodes(Codes.getHairColorCodes() || []);
      setLocStates(Codes.getStates() || []);

      // Filter race codes by company country
      const companyCountry = typeof (window as any).getCompanyCountry === 'function' ? getCompanyCountry() : 'US';
      const allRaceCodes = Codes.getRaceCodes() || [];
      const filtered = allRaceCodes.filter((elt: any) => {
        return (typeof isInArray === 'function' && isInArray(elt.visible, companyCountry)) ||
               (hasValue(personRef.current?.race) && elt.code.toString() === personRef.current?.race?.toString());
      });
      filtered.sort((a: any, b: any) => a.order - b.order);
      setRaceCodes(filtered);

      // Relationship types
      if (typeof Codes.getRelationshipTypes === 'function') {
        setRelationshipTypes(Codes.getRelationshipTypes() || []);
      }
    }

    const hu = (window as any).heightUnits;
    if (typeof hu !== 'undefined') {
      setHeightUnitsList(hu);
    }
    const wu = (window as any).weightUnits;
    if (typeof wu !== 'undefined') {
      setWeightUnitsList(wu);
    }
    const pot = (window as any).PhoneOptionTypes;
    if (typeof pot !== 'undefined') {
      setPhoneOptionTypes(pot);
    }
    const eot = (window as any).EmailOptionTypes;
    if (typeof eot !== 'undefined') {
      setEmailOptionTypes(eot);
    }
  }, []);

  // ── Mount / Unmount ─────────────────────────────────────────────────
  useEffect(() => {
    // Mobile check (debounced to avoid excessive re-renders during resize)
    let mobileTimer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (mobileTimer) clearTimeout(mobileTimer);
      mobileTimer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    loadCodeLists();

    if (typeof (window as any).showUsaStateInformation === 'function') {
      setShowUsaStateInfo(showUsaStateInformation());
    }

    if (hasValue(personIdProp)) {
      loadPerson(personIdProp!.toString());
    } else {
      initNewPerson();
    }

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Watch personId prop changes ─────────────────────────────────────
  const prevPersonIdRef = useRef(personIdProp);
  useEffect(() => {
    if (prevPersonIdRef.current !== personIdProp) {
      prevPersonIdRef.current = personIdProp;
      if (hasValue(personIdProp)) {
        loadPerson(personIdProp!.toString());
      } else {
        initNewPerson();
      }
    }
  }, [personIdProp, loadPerson, initNewPerson]);

  // ── Keep global currentPerson in sync ───────────────────────────────
  useEffect(() => {
    if (!isWindow && person) {
      currentPerson = person;
      (window as any).currentPerson = person;
    }
  }, [person, isWindow]);

  // ── Edit/Save/Cancel lifecycle ──────────────────────────────────────

  const enterEditMode = useCallback(() => {
    if (!person?.id) return;
    setNPSDItemLocked(person.id.toString(), true, 'person', () => {
      setIsEditMode(true);
      setIsSaveMode(false);
    });
  }, [person?.id]);

  const cancelEdit = useCallback(() => {
    if (!person?.id) {
      if (isWindow) {
        onClose?.();
      }
      return;
    }

    setNPSDItemLocked(person.id.toString(), false, 'person', () => {
      // Check if there are unsaved changes
      const hasChanges = JSON.stringify(person) !== JSON.stringify(originalPerson);
      if (hasChanges) {
        setPerson(JSON.parse(JSON.stringify(originalPerson)));
      }
      setIsEditMode(false);

      if (isWindow) {
        onClose?.();
      }
    });
  }, [person, originalPerson, isWindow, onClose]);

  const validatePerson = useCallback((): boolean => {
    if (!person?.firstName || !person?.lastName) {
      App?.showPopupNotification('First name and last name are required.', 'warning');
      return false;
    }
    return true;
  }, [person]);

  const calculateHeightWeight = useCallback((p: any): any => {
    const updated = { ...p, height: { ...p.height }, weight: { ...p.weight } };

    // Calculate height in mm
    if (updated.height.displayUnits === 'm' || updated.height.displayUnits === 'meters') {
      const meters = parseFloat(updated.personHeightMeters);
      updated.height.mm = isNaN(meters) ? null : Math.round(meters * 1000);
    } else {
      const feet = parseInt(updated.personHeightFeet) || 0;
      const inches = parseInt(updated.personHeightInches) || 0;
      const totalInches = feet * 12 + inches;
      updated.height.mm = totalInches > 0 ? Math.round(totalInches * 25.4) : null;
    }

    // Calculate weight in grams
    const weightVal = parseFloat(updated.personWeight);
    if (!isNaN(weightVal) && weightVal > 0) {
      if (updated.weight.displayUnits === 'kg') {
        updated.weight.grams = Math.round(weightVal * 1000);
      } else {
        updated.weight.grams = Math.round(weightVal * 453.592);
      }
    } else {
      updated.weight.grams = null;
    }

    return updated;
  }, []);

  const preparePersonForSave = useCallback((p: any): any => {
    const personCopy = JSON.parse(JSON.stringify(p));

    // Clear display units if no value
    if (!personCopy.height.mm) {
      personCopy.height.displayUnits = null;
    }
    if (!personCopy.weight.grams) {
      personCopy.weight.displayUnits = null;
    }

    // Remove computed fields
    delete personCopy.personHeightFeet;
    delete personCopy.personHeightInches;
    delete personCopy.personHeightMeters;
    delete personCopy.personWeight;

    return personCopy;
  }, []);

  const savePerson = useCallback(async () => {
    if (!validatePerson()) return;

    // Calculate height/weight
    const withHeightWeight = calculateHeightWeight(personRef.current);

    // Validate and fill address
    const addressFields = await validateAndFillAddress(personRef.current?.locFormattedAddress, originalPerson);
    if (!addressFields) return;

    // Merge address fields into the person (state update is async, so merge directly)
    const currentPerson = { ...personRef.current, ...addressFields };
    const withCalc = calculateHeightWeight(currentPerson);
    const withConnections = ensureConnectionsNewPopulated(withCalc);
    const personToSave = preparePersonForSave(withConnections);

    if (!hasValue(currentPerson.id)) {
      // Save new person
      api.saveNPSDItem(personToSave, 'person', (result: any) => {
        setOriginalPerson(null);
        if (typeof App !== 'undefined') {
          App.originalPersonResult = null;
          App?.showPopupNotification('Person saved successfully.', 'success');
        }

        if (!isCaseReportMode && !isWindow) {
          refreshWithParams(result.id);
        } else {
          onPersonSaved?.(result);
          if (isBasePageCaseReport()) {
            const addToReport = (window as any).NPSDItemToReport;
            if (typeof addToReport === 'function') addToReport('lnkRptAddPerson', result.id);
            (window as any).CaseRpt?.currentMDTRptController?.closeInvolvedSearchModal();
          }
        }
      });
    } else {
      // Update attachment titles if changed
      for (const item of currentPerson.attachmentList) {
        const origItem = originalPerson?.attachmentList?.find((d: any) => d.id === item.id);
        if ((origItem && item.title !== origItem.title) || !origItem) {
          api.updateAttachment(item.title, item.id, () => {});
        }
      }

      // Update existing person
      api.updateNPSDItem(personToSave, 'person', (result: any) => {
        // Handle case report context
        if ((window as any).CaseRpt?.currentMDTRptController?.isSearchOpen()) {
          (window as any).CaseRpt?.currentMDTRptController.closeInvolvedSearchModal();
        }

        setOriginalPerson(null);
        if (typeof App !== 'undefined') {
          App.originalPersonResult = null;
          App?.showPopupNotification('Person updated successfully.', 'success');
        }

        setIsEditMode(false);

        if (!isWindow && !isBasePageCaseReport() && !isBasePageCallCard() && !isBasePageCitation()) {
          refreshPage();
        }

        if (typeof App !== 'undefined') {
          App.pauseItemChangeCheck = true;
        }

        if (isBasePageCaseReport()) {
          updateNPSDItemForReport(RelType.person, currentPerson);
        } else if (isBasePageCallCard()) {
          if (App.callCardController?.showPersonModal) {
            const uuid = App.callCardController.personModalUuid;
            if (uuid) {
              updateNPSDItemForCallCard(RelType.person, currentPerson, uuid);
            }
            App.callCardController.closePersonModal();
          }
        } else if (isBasePageCitation()) {
          const citationState = (window as any).citationFormState as any;
          if (citationState?.showLinkedPersonModal) {
            citationState.closeLinkedPersonModal();
          }
        }

        if (isWindow) {
          onPersonUpdated?.(result);
          onClose?.();
        }
      }, (error: any) => {
        setIsEditMode(true);
        App?.showPopupNotification('Failed to update person.', 'error');
      });
    }
  }, [
    validatePerson, calculateHeightWeight, validateAndFillAddress,
    ensureConnectionsNewPopulated, preparePersonForSave,
    isCaseReportMode, isWindow, originalPerson,
    onPersonSaved, onPersonUpdated, onClose,
  ]);

  // ── Person update handler (from child details component) ────────────
  const handlePersonUpdate = useCallback((updated: any) => {
    setPerson(updated);
  }, []);

  // ── Height/weight unit change handlers ──────────────────────────────
  const onHeightUnitChange = useCallback(() => {
    setPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, height: { ...prev.height } };

      if (updated.height.displayUnits === 'm' || updated.height.displayUnits === 'meters') {
        const feet = parseInt(updated.personHeightFeet) || 0;
        const inches = parseInt(updated.personHeightInches) || 0;
        const totalInches = feet * 12 + inches;
        if (totalInches > 0) {
          updated.height.mm = Math.round(totalInches * 25.4);
          updated.personHeightMeters = (updated.height.mm * 0.001).toFixed(2);
        } else {
          updated.personHeightMeters = '';
        }
        updated.personHeightFeet = '';
        updated.personHeightInches = '';
      } else {
        const meters = parseFloat(updated.personHeightMeters);
        if (!isNaN(meters) && meters > 0) {
          updated.height.mm = Math.round(meters * 1000);
          const totalInches = Math.round(updated.height.mm / 25.4);
          updated.personHeightFeet = Math.floor(totalInches / 12).toString();
          updated.personHeightInches = (totalInches % 12).toString();
        } else {
          updated.personHeightFeet = '';
          updated.personHeightInches = '';
        }
        updated.personHeightMeters = '';
      }
      return updated;
    });
  }, []);

  const onWeightUnitChange = useCallback(() => {
    setPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, weight: { ...prev.weight } };
      const weightVal = parseFloat(updated.personWeight);
      if (!isNaN(weightVal) && weightVal > 0) {
        if (updated.weight.displayUnits === 'kg') {
          updated.personWeight = (weightVal / 2.205).toFixed(1);
        } else {
          updated.personWeight = (weightVal * 2.205).toFixed(1);
        }
      }
      return updated;
    });
  }, []);

  // ── Map view ────────────────────────────────────────────────────────
  const viewMap = useCallback(() => {
    if (!person?.locFormattedAddress) return;
    navigateToMapByAddress(person.locFormattedAddress);
  }, [person?.locFormattedAddress]);

  // ── View NPSD item - open in PageModal ──────────────────────────────
  const viewNpsdItem = useCallback((type: string, item: any) => {
    const id = typeof item === 'object' ? item.id : item;
    switch (type) {
      case 'person': openViewPersonModal(id); break;
      case 'vehicle': openViewVehicleModal(id); break;
      case 'business': openViewBusinessModal(id); break;
      case 'client': openViewClientModal(id); break;
    }
  }, [openViewPersonModal, openViewVehicleModal, openViewBusinessModal, openViewClientModal]);

  // ── Confirm modal helpers ───────────────────────────────────────────
  const openConfirmModal = useCallback((title: string, message: string, onConfirm: () => void) => {
    setConfirmModal({
      open: true,
      title,
      message,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm,
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmModal.onConfirm) {
      confirmModal.onConfirm();
    }
    setConfirmModal((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, [confirmModal.onConfirm]);

  const closeConfirmModal = useCallback(() => {
    setConfirmModal((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const addConnectionToEntity = useCallback((type: string, item: any, relationship?: number | null, notes?: string) => {
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type === 'client' ? 'clients' :
                       type + 's';

    let connection: any = { id: item.id };

    if (type === 'person') {
      connection = {
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        dob: item.dob,
        relationship: relationship || null,
        notes: notes || '',
      };
    } else if (type === 'vehicle') {
      connection = {
        id: item.id,
        plateNumber: item.plateNumber,
        plateState: item.plateState,
        make: item.make,
        model: item.model,
        color: item.color,
        vin: item.vin,
      };
    } else if (type === 'business') {
      connection = {
        id: item.id,
        businessName: item.businessName || item.name,
        branchName: item.branchName,
        website: item.website,
        roles: '',
      };
    } else if (type === 'client') {
      connection = {
        id: item.id,
        name: item.name,
        locCity: item.locCity,
        locState: item.locState,
        website: item.website,
        roles: '',
      };
    }

    setPerson((prev: any) => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.connections[pluralType]) updated.connections[pluralType] = [];
      if (!updated.connectionsNew[pluralType]) updated.connectionsNew[pluralType] = [];
      updated.connections[pluralType].push(connection);
      updated.connectionsNew[pluralType].push(connection);
      return updated;
    });

    App?.showPopupNotification('Connection added.', 'success');
    closeConnectionSearchModal();
  }, [closeConnectionSearchModal]);

  const handleConnectionSelect = useCallback((item: any) => {
    const type = connectionSearchType;
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type === 'client' ? 'clients' :
                       type + 's';

    const existingArray = person?.connections?.[pluralType] || [];
    const isDuplicate = existingArray.some((c: any) => c.id === item.id);

    if (isDuplicate) {
      App?.showPopupNotification('This connection already exists.', 'warning');
      return;
    }

    // Check for self-reference
    if (type === 'person' && item.id === person?.id) {
      App?.showPopupNotification('Cannot connect a person to themselves.', 'warning');
      return;
    }

    // For person connections, show relationship dialog
    if (type === 'person') {
      setPendingConnectionItem(item);
      setSelectedRelationship(null);
      setConnectionNotes('');
      closeConnectionSearchModal();
      setShowRelationshipDialog(true);
      return;
    }

    // Add connection directly for non-person types
    addConnectionToEntity(type, item);
  }, [connectionSearchType, person, addConnectionToEntity, closeConnectionSearchModal]);

  // ── Relationship dialog ─────────────────────────────────────────────
  const confirmRelationshipDialog = useCallback(() => {
    if (!selectedRelationship) {
      App?.showPopupNotification('Please select a relationship.', 'warning');
      return;
    }
    if (pendingConnectionItem) {
      addConnectionToEntity('person', pendingConnectionItem, selectedRelationship, connectionNotes);
    }
    setShowRelationshipDialog(false);
    setPendingConnectionItem(null);
    setSelectedRelationship(null);
    setConnectionNotes('');
  }, [selectedRelationship, pendingConnectionItem, connectionNotes, addConnectionToEntity]);

  const closeRelationshipDialog = useCallback(() => {
    setShowRelationshipDialog(false);
    setPendingConnectionItem(null);
    setSelectedRelationship(null);
    setConnectionNotes('');
  }, []);


  // ── View modal close handlers ───────────────────────────────────────
  const closeViewPersonModal = useCallback(() => {
    setShowViewPersonModal(false);
    // Reload current person to pick up any changes made in the view modal
    if (person?.id) {
      loadPerson(String(person.id));
    }
  }, [person?.id, loadPerson, setShowViewPersonModal]);

  const closeViewVehicleModal = useCallback(() => {
    setShowViewVehicleModal(false);
    if (person?.id) {
      loadPerson(String(person.id));
    }
  }, [person?.id, loadPerson, setShowViewVehicleModal]);

  const closeViewBusinessModal = useCallback(() => {
    setShowViewBusinessModal(false);
    if (person?.id) {
      loadPerson(String(person.id));
    }
  }, [person?.id, loadPerson, setShowViewBusinessModal]);

  const closeViewClientModal = useCallback(() => {
    setShowViewClientModal(false);
    if (person?.id) {
      loadPerson(String(person.id));
    }
  }, [person?.id, loadPerson, setShowViewClientModal]);

  // ── Warnings management ─────────────────────────────────────────────
  const handleWarningsChange = useCallback((updatedWarnings: any[]) => {
    setPerson((prev: any) => {
      if (!prev) return prev;
      return { ...prev, warnings: updatedWarnings };
    });
  }, []);

  // ── Attachment management ───────────────────────────────────────────
  const updateProfilePic = useCallback((attachmentId: number) => {
    setPerson((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        descriptionPicInfoId: attachmentId,
        descriptionPicId: attachmentId,
      };
    });

    const actionText = person?.id ? 'Update' : 'Save';
    App?.showPopupNotification(`Picture set. Please click ${actionText} to save changes.`, 'success');
  }, [person?.id]);

  const addAttachment = useCallback(() => {
    if (!isSaveMode && !isEditMode) {
      enterEditMode();
    }
    if (attachmentsFileInputRef.current) {
      attachmentsFileInputRef.current.click();
    }
  }, [isSaveMode, isEditMode, enterEditMode]);

  const editProfilePic = useCallback(() => {
    if (!canEditProfilePic) return;
    if (!isSaveMode && !isEditMode) {
      enterEditMode();
    }
    if (profilePicInputRef.current) {
      profilePicInputRef.current.click();
    }
  }, [canEditProfilePic, isSaveMode, isEditMode, enterEditMode]);

  const handleProfilePicUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) return;

    if (typeof App !== 'undefined' && App.uploadFromFileInput) {
      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (idx: number, attachmentInfo: any) => {
          setPerson((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              attachmentInfoList: [...prev.attachmentInfoList, attachmentInfo],
            };
          });
        },
        updateAttachment: (idx: number, attachment: any) => {
          setPerson((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              attachmentList: [...prev.attachmentList, attachment],
            };
          });
        },
        successCallback: (idx: number, attachment: any) => {
          setPerson((prev: any) => {
            if (!prev) return prev;
            const updatedList = prev.attachmentList.map((a: any) => {
              if (a.id === attachment.id) {
                return { ...a, signedURL: attachment.signedURL + '#' + new Date().getTime() };
              }
              return a;
            });
            // Also set as profile pic
            return {
              ...prev,
              attachmentList: updatedList,
              descriptionPicId: attachment.id,
              descriptionPicInfoId: attachment.id,
            };
          });

          const actionText = personRef.current?.id ? 'Update' : 'Save';
          App?.showPopupNotification(`Picture set. Please click ${actionText} to save changes.`, 'success');
        },
      });
    }
  }, []);

  // ── Render ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={`npsd-person-screen-wrapper ${theme}`}>
        <div className={`npsd-entity-screen npsd-person-screen ${theme}`}>
          <div className="loading-container"><div className="loading-spinner"></div></div>
        </div>
      </div>
    );
  }

  if (!person) return null;

  return (
    <div className={`npsd-person-screen-wrapper ${theme}`}>
      <div className={`npsd-entity-screen npsd-person-screen ${theme}`}>
        {/* Profile Header */}
        <div className="profile-card-header">
          <div className="profile-cover-details">
            <div className="profile-image-container">
              <a
                className={`profile-image${!canEditProfilePic ? ' vue-disabled' : ''}`}
                onClick={editProfilePic}
              >
                <img src={profilePicUrl} alt="Person" />
              </a>
            </div>
            <div className="profile-info">
              <h3 className="person-title">{personDisplayName}</h3>
            </div>
          </div>

          <div className="action-buttons">
            {showEditButton && (
              <button className="btn btn-header" onClick={enterEditMode}>Edit</button>
            )}
            {!isOwnCompanyPerson && person.createdByCompanyName && (
              <span className="created-by">{person.createdByCompanyName}</span>
            )}
            {showUpdateButton && (
              <button className="kstc-btn kstc-btn--success" onClick={savePerson}>Update</button>
            )}
            {showCancelButton && (
              <button className="kstc-btn kstc-btn--danger" onClick={cancelEdit}>Cancel</button>
            )}
            {showSaveButton && (
              <button className="kstc-btn kstc-btn--success" onClick={savePerson}>Save</button>
            )}
            {showCaseReportSaveButton && (
              <button className="kstc-btn kstc-btn--success" onClick={savePerson}>Save/Add</button>
            )}
          </div>
          <NPSDWarningsDisplay warnings={person.warnings || []} />
        </div>

        {/* Tab Navigation */}
        <NPSDTabBar
          tabs={tabsConfig}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div className="person-tab-content">
          <div className="person-tab-panel" style={{ display: activeTab === 'person' ? undefined : 'none' }}>
            <NPSDPersonDetails
              person={person}
              isEditMode={enableFormField}
              genderCodes={genderCodes}
              raceCodes={raceCodes}
              eyeColorCodes={eyeColorCodes}
              hairColorCodes={hairColorCodes}
              heightUnits={heightUnitsList}
              weightUnits={weightUnitsList}
              locStates={locStates}
              phoneOptionTypes={phoneOptionTypes}
              emailOptionTypes={emailOptionTypes}
              showUsaStateInformation={showUsaStateInfo}
              onUpdate={handlePersonUpdate}
              onHeightUnitChange={onHeightUnitChange}
              onWeightUnitChange={onWeightUnitChange}
              onViewMap={viewMap}
            />
          </div>
          <div className="person-tab-panel" style={{ display: activeTab === 'connections' ? undefined : 'none' }}>
            <NPSDPersonConnections
              person={person}
              isEditMode={enableFormField}
              onViewConnection={viewNpsdItem}
              onAddConnection={openConnectionSearch}
            />
          </div>
          <div className="person-tab-panel" style={{ display: activeTab === 'history' ? undefined : 'none' }}>
            <NPSDPersonHistory
              personId={person.id}
              reports={person.connections?.reports || []}
              active={activeTab === 'history'}
            />
          </div>
          <div className="person-tab-panel" style={{ display: activeTab === 'attachments' ? undefined : 'none' }}>
            <NPSDAttachments
              attachmentList={person.attachmentList}
              isEditMode={showAddAttachmentButton}
              theme={theme}
              showProfilePicOption={true}
              profilePicId={person.descriptionPicId}
              onAdd={addAttachment}
              onRemove={removeAttachment}
              onSetProfilePic={updateProfilePic}
            />
          </div>
          <div className="person-tab-panel" style={{ display: activeTab === 'warnings' ? undefined : 'none' }}>
            <NPSDWarnings
              warnings={person.warnings}
              isReadOnly={!enableFormField}
              title="Warnings"
              onChange={handleWarningsChange}
            />
          </div>
          <div className="person-tab-panel" style={{ display: activeTab === 'citations' ? undefined : 'none' }}>
            {canViewCitations && (
              <PersonCitationsGrid
                personId={person.id}
                title="Citations"
                isReadOnly={!isEditMode && !isSaveMode}
              />
            )}
          </div>
          <div className="person-tab-panel" style={{ display: activeTab === 'auditlog' ? undefined : 'none' }}>
            <NPSDAuditLog
              entityId={person.id}
              entityType="person"
              title="Audit Log"
            />
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={profilePicInputRef}
          onChange={handleProfilePicUpload}
          style={{ display: 'none' }}
        />
        <input
          type="file"
          ref={attachmentsFileInputRef}
          onChange={handleAttachmentUpload}
          multiple
          style={{ display: 'none' }}
        />

        {/* Confirm Modal */}
        <ConfirmModal
          open={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          onConfirm={handleConfirm}
          onCancel={closeConfirmModal}
        />

        {/* Connection Search Modal */}
        <SearchModal
          open={showConnectionSearchModal}
          searchType={connectionSearchType as any}
          title={'Add ' + connectionSearchTypeLabel + ' Connection'}
          theme={theme}
          onSelect={handleConnectionSelect}
          onView={handleConnectionView}
          onCancel={closeConnectionSearchModal}
        />

        {/* Relationship Dialog for Person-to-Person Connections */}
        <ConfirmModal
          open={showRelationshipDialog}
          title="Add Person Connection"
          confirmText="Add"
          cancelText="Cancel"
          confirmDisabled={!selectedRelationship}
          onConfirm={confirmRelationshipDialog}
          onCancel={closeRelationshipDialog}
        >
          <div className={`relationship-dialog-content ${theme}`}>
            {pendingConnectionItem && (
              <p className="kstc-message" style={{ marginBottom: '15px' }}>
                Adding connection to: <strong>{pendingConnectionItem.firstName} {pendingConnectionItem.lastName}</strong>
              </p>
            )}
            <div className="kstc-field" style={{ marginBottom: '15px' }}>
              <label className="kstc-field__label">Relationship</label>
              <Dropdownlist
                value={selectedRelationship}
                items={relationshipTypes}
                labelKey="value"
                valueKey="code"
                placeholder="-- Select --"
                searchable={false}
                sortItems={false}
                onChange={(val) => setSelectedRelationship(val as number | null)}
              />
            </div>
            <div className="kstc-field">
              <label className="kstc-field__label">Notes</label>
              <textarea
                value={connectionNotes}
                className="kstc-input"
                rows={3}
                placeholder="Optional notes about this connection"
                onChange={(e) => setConnectionNotes(e.target.value)}
              />
            </div>
          </div>
        </ConfirmModal>

        {/* View Person Modal */}
        <PageModal
          open={showViewPersonModal}
          title="View Person"
          modalId="npsdViewPerson"
          onClose={closeViewPersonModal}
        >
          {showViewPersonModal && viewPersonId && (
            <NPSDPersonScreen
              personId={viewPersonId}
              isWindow={true}
              onClose={closeViewPersonModal}
            />
          )}
        </PageModal>

        {/* View Vehicle Modal */}
        <PageModal
          open={showViewVehicleModal}
          title="View Vehicle"
          modalId="npsdViewVehicle"
          onClose={closeViewVehicleModal}
        >
          {showViewVehicleModal && viewVehicleId && (
            <NPSDVehicleScreen
              vehicleId={viewVehicleId}
              isWindow={true}
              onClose={closeViewVehicleModal}
            />
          )}
        </PageModal>

        {/* View Business Modal */}
        <PageModal
          open={showViewBusinessModal}
          title="View Business"
          modalId="npsdViewBusiness"
          onClose={closeViewBusinessModal}
        >
          {showViewBusinessModal && viewBusinessId && (
            <NPSDBusinessScreen
              businessId={viewBusinessId}
              isModal={true}
              onClose={closeViewBusinessModal}
            />
          )}
        </PageModal>

        {/* View Client Modal */}
        <PageModal
          open={showViewClientModal}
          title="View Client"
          modalId="npsdViewClient"
          onClose={closeViewClientModal}
        >
          {showViewClientModal && viewClientId && (
            <NPSDClientScreenWrapper
              clientId={String(viewClientId)}
            />
          )}
        </PageModal>
      </div>
    </div>
  );
}

// ── Screen registry ──────────────────────────────────────────────────────
